import { Backdrop, useNotificationModal } from '@room-match/shared-ui-components'; // Remove Styled Dialog
import { useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { ROUTES } from 'shared/constants/ROUTES';
import { ProfileMainCont } from './Profiles.styles';
import { RepresentationTypeGraphQL } from 'shared/enums/RepresentationTypeGraphQL';
import { ProgressProps } from 'shared/hooks/PetsCars/useSubmitForm';
import { IProfileGraphQL } from 'shared/interfaces/IProfile';
import { setUser } from 'shared/redux/slicers/user.slicer';
import { useAppSelector } from 'shared/redux/store';
import { profileService } from 'shared/services/profileService';
import { useSetHeaderBack, useSetHeaderTitle } from 'shared/hooks/useSetHeader';
import StyledDialog from 'shared/elements/StyledDialog/StyledDialog'; // Use talent app level folder StyledDialog - Jerome
import useWindowDimensions from 'shared/hooks/useWindowDimenstion';
import AddModal from './AddProfileModal';
import EditProfile from './EditProfile/EditProfile';
import ProfileItem from './ProfileItem';

const { getProfilesGraphQL, updateProfile } = profileService();

function Profiles() {
  const dispatch = useDispatch();
  const { user: userState } = useAppSelector((state) => state);

  useSetHeaderTitle(`Representatives`);

  const { data, isLoading } = getProfilesGraphQL('');
  const profiles = useMemo(() => {
    return data?.data.profiles;
  }, [data]);

  const [addModal, setAddModal] = useState(false);
  const { profileId } = useParams() as { tab: string; profileId: string };
  const { mutateAsync } = updateProfile();
  const { NotificationModal, isOpen, handleOpen, handleSetMessage } = useNotificationModal({ type: 'warning' });
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<ProgressProps>('idle');
  const [activeProgressId, setActiveProgressId] = useState<string | null>(null);
  const history = useHistory();
  const { width } = useWindowDimensions();
  const hasFreelance = !!profiles?.some(
    (data) => data.representation_type === RepresentationTypeGraphQL.FREELANCE && !data.casting_company,
  );

  const urlSplitCount = window.location.pathname.split('/').length;
  useSetHeaderBack(urlSplitCount > 3);
  const primaryId = profiles?.find((f) => f.primary)?.id;

  const handleClick = (id: string) => {
    history.push(`${ROUTES.TALENT.PROFILES}/${id}/images_video_audio`);
  };

  const removePrimary = async (profileId: string, invalidation?: any) => {
    await mutateAsync({ profileId: profileId || '', payload: { primary: false } }, invalidation);
  };

  const handleMakePrimary = async (profileId: string, isPrimary: boolean, image: string | undefined) => {
    // This condition removes duplicated primary statuses on the Representatives Page. If an item is marked as primary but its ID does not match the primaryId,
    // it indicates a duplicate primary item, which should not occur.
    if (primaryId !== profileId && isPrimary) {
      return await removePrimary(profileId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries('profiles');
          await queryClient.invalidateQueries('me', {
            refetchActive: true,
            refetchInactive: true,
          });
          setProgress('idle');
        },
      });
    }

    // If the selected Item is Primary and ID match just simply return, because it is the actual primary
    if (primaryId === profileId && isPrimary) return;

    // If progress is Saving just return and open the modal so it wont cause duplicate primary items
    if (progress === 'saving') {
      handleSetMessage('Action in Progress!', "You can't perform this action for now!", 'Close');
      handleOpen();
      return;
    } else {
      setProgress('saving');
      setActiveProgressId(profileId);
    }

    // Remove the status of actual Primary Item
    if (primaryId) await removePrimary(primaryId);

    // Make the current Item Primary
    mutateAsync(
      { profileId, payload: { primary: true } },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries('profiles');
          // We are not using invalidation to get the latest data from ME endpoint
          // Instead manually update it in Dispatch in Redux
          dispatch(
            setUser({
              user: {
                ...userState.user,
                attributes: {
                  ...userState.user.attributes,
                  picture: image || '',
                },
              },
            }),
          );
          setProgress('idle');
        },
      },
    );
  };

  return isLoading ? (
    <Backdrop isLoading={isLoading} />
  ) : profileId ? (
    <EditProfile profiles={profiles} />
  ) : (
    <ProfileMainCont>
      {isOpen ? <NotificationModal /> : null}
      <StyledDialog
        fullScreen={width < 960}
        style={{ margin: 10 }}
        open={addModal}
        onClose={() => setAddModal(false)}
        maxWidth="md"
        aria-labelledby="simple-dialog-title"
      >
        <AddModal hasFreelance={hasFreelance} onClose={() => setAddModal(false)} />
      </StyledDialog>
      {data?.data.profiles.map((profile: IProfileGraphQL, idx: number) => (
        <ProfileItem
          primaryId={primaryId || ''}
          click={handleClick}
          isLoading={progress === 'saving' && profile?.id === activeProgressId}
          handleMakePrimary={handleMakePrimary}
          profile={profile}
          key={profile.id + '-' + idx}
        />
      ))}
      <ProfileItem addModal={() => setAddModal(true)} primaryId={primaryId || ''} handleMakePrimary={() => {}} />
    </ProfileMainCont>
  );
}

export default Profiles;
